import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { SUBSCRIPTION_CONFIGS } from '@cx-web/repub-config';
import { getNoCacheFromRequest } from '@cx-web/utils/getNoCacheFromRequest';
import { ClientOnly } from 'remix-utils/client-only';

import { type MatchesCouponIndexProps } from '@cx-modules/matches-coupon';
import { type MoreArticlesIndexProps } from '@cx-modules/more-articles';
import { type OffersListIndexProps } from '@cx-modules/offers-list';
import { type OutrightsCouponIndexProps } from '@cx-modules/outrights-coupon';
import { type TextBlockIndexProps } from '@cx-modules/text-block';

import {
  MatchesCoupon,
  MoreArticles,
  OffersCarousel,
  OffersCarouselContentContractForPageTemplate,
  OffersGrouped,
  OffersGroupedContentContractForPageTemplate,
  OffersList,
  OffersSlim,
  OffersSlimContentContractForPageTemplate,
  OutrightsCoupon,
  PremiumSignup,
  type PremiumSignupProps,
  SubeventHeader,
  TextBlock,
} from '~/modules';
import { useMyOCAuth } from '~/providers/myoc-auth';
import ArticleFooter from '~/routes/$/components/ArticleFooter';
import ArticleHeader, { type ArticleHeaderProps } from '~/routes/$/components/ArticleHeader';
import BannersCarousel from '~/routes/$/components/BannersCarousel';
import Breadcrumbs from '~/routes/$/components/Breadcrumbs';
import { GridGroup } from '~/routes/$/components/GridGroup/GridGroup';
import { IconListSection } from '~/routes/$/components/IconListSection';
import InlineHtml from '~/routes/$/components/InlineHtml';
import { LandingPageFooter } from '~/routes/$/components/LandingPageFooter/LandingPageFooter';
import { LandingPageHeader } from '~/routes/$/components/LandingPageHeader/LandingPageHeader';
import Onboarding, { type OnboardingProps } from '~/routes/$/components/Onboarding';
import ParentLinkHeader, { type ParentLinkHeaderProps } from '~/routes/$/components/ParentLinkHeader';
import { ReviewsSection } from '~/routes/$/components/ReviewsSection';
import OCConnectWidget, { type OCConnectWidgetProps } from '~/routes/$/components/Widgets/OCConnect';
import { OffersWidget, type OffersWidgetProps } from '~/routes/$/components/Widgets/Offers';
import { getSubeventId } from '~/routes/$/utils';
import { type ComponentTypeNames } from '~/types/template';
import { getPathExcludingRepubFromUrl } from '~/utils/getPathExcludingRepubFromUrl';

import { type ClientComponentMap, componentTypeNames } from './component-map.types';
import { ocConnectWidgetScript, ocOffersWidgetScript } from './scripts';

/**
 * Helper function to get the CX API URL
 * Returns the API URL during SSR (server-side rendering), undefined in browser
 * Components use this for @cx-modules that need the API endpoint for client-side fetching
 */
const getServerCxApiUrl = () => {
  const isServer = typeof window === 'undefined';
  return isServer ? ENV.CX_API_URL : undefined;
};

/**
 * Component types that represent groups of components
 * Used by isGroupComponent utility
 */
export const groupComponents: Partial<Record<keyof ComponentTypeNames, true>> = {
  GRID_GROUP: true,
};

/**
 * Re-export component type names for convenience
 */
export { componentTypeNames };

/**
 * Client-side component map containing React components for all dynamic page template types.
 * These components receive hydrated data from the server loader functions.
 */
export const componentMapClient: ClientComponentMap = {
  ARTICLES_WIDGET: {
    name: 'ARTICLES_WIDGET',
    Component: (props: MoreArticlesIndexProps) => {
      const location = useLocation();
      const params = new URLSearchParams(location.search);
      const articlesPage = Number(params.get('articlesPage')) || props.articlesPage || 1;
      const nocache = props.nocache || getNoCacheFromRequest();

      // reload the page when the user uses the browser back tab
      // to ensure pagination UI state syncs
      useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
          if (event.persisted) {
            window.location.reload();
          }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
      }, []);

      const { myocData } = useMyOCAuth();
      const isLoggedIn = !!myocData.myOCUser;

      const dataApiEndpoint = getServerCxApiUrl();
      return (
        <MoreArticles
          {...props}
          articlesPage={articlesPage}
          dataApiEndpoint={dataApiEndpoint}
          isLoggedIn={isLoggedIn}
          nocache={nocache}
        />
      );
    },
  },
  BANNERS_CAROUSEL: {
    name: 'BANNERS_CAROUSEL',
    Component: props => <BannersCarousel {...props} />,
  },
  BREADCRUMBS: {
    name: 'BREADCRUMBS',
    Component: props => <Breadcrumbs {...props} />,
  },
  EVERGREEN_TEXT_BLOCK: {
    name: 'EVERGREEN_TEXT_BLOCK',
    Component: (props: TextBlockIndexProps) => {
      const dataApiEndpoint = getServerCxApiUrl();
      const nocache = props.nocache || getNoCacheFromRequest();
      return <TextBlock {...props} dataApiEndpoint={dataApiEndpoint} nocache={nocache} />;
    },
  },
  GRID_GROUP: {
    name: 'GRID_GROUP',
    Component: props => <GridGroup {...props} />,
  },
  ICON_LIST: {
    name: 'ICON_LIST',
    Component: props => <IconListSection {...props} />,
  },
  LANDING_PAGE_FOOTER: {
    name: 'LANDING_PAGE_FOOTER',
    Component: props => <LandingPageFooter {...props} />,
  },
  LANDING_PAGE_HEADER: {
    name: 'LANDING_PAGE_HEADER',
    Component: props => <LandingPageHeader {...props} />,
  },
  INLINE_HTML: {
    name: 'INLINE_HTML',
    Component: (props: { componentTags?: string[]; html: string }) => <InlineHtml {...props} />,
  },
  ARTICLE_FOOTER_V1: {
    name: 'ARTICLE_FOOTER_V1',
    Component: props => <ArticleFooter {...props} />,
  },
  ARTICLE_HEADER_V1: {
    name: 'ARTICLE_HEADER_V1',
    Component: (props: ArticleHeaderProps) => <ArticleHeader {...props} />,
  },
  MATCHES_COUPON: {
    name: 'MATCHES_COUPON',
    Component: (props: MatchesCouponIndexProps) => {
      const nocache = props.nocache || getNoCacheFromRequest();
      return <MatchesCoupon {...props} nocache={nocache} />;
    },
  },
  OC_CONNECT_WIDGET: {
    name: 'OC_CONNECT_WIDGET',
    Component: (props: OCConnectWidgetProps) => <ClientOnly>{() => <OCConnectWidget {...props} />}</ClientOnly>,
    scripts: [ocConnectWidgetScript],
  },
  OFFERS_WIDGET: {
    name: 'OFFERS_WIDGET',
    Component: (props: OffersWidgetProps) => <ClientOnly>{() => <OffersWidget {...props} />}</ClientOnly>,
    scripts: [ocOffersWidgetScript],
  },
  OFFERS_PAGE: {
    name: 'OFFERS_PAGE',
    Component: ({ deviceType, nocache, requestUrl, subdivision, ...props }) => {
      const clientNoCache = getNoCacheFromRequest();
      const offersListProps: OffersListIndexProps = {
        ...props,
        apiKey: ENV.OC_API_KEY[props.repub as keyof typeof ENV.OC_API_KEY],
        deviceTypeForApi: deviceType,
        fullPageUrl: requestUrl,
        path: getPathExcludingRepubFromUrl(props.repub, requestUrl),
        subdivisionCode: subdivision,
        nocache: nocache || clientNoCache,
        geoSubdivisionCode: props.geoLocationInfo.geoSubdivisionCode,
      };

      return (
        <div className="ogm-offers-list-wrapper">
          <div className="ogm-offers-list-content">
            <OffersList {...offersListProps} />
          </div>
        </div>
      );
    },
  },
  OFFERS_CAROUSEL: {
    name: 'OFFERS_CAROUSEL',
    Component: props => {
      const nocache = props.nocache || getNoCacheFromRequest();
      return <OffersCarousel {...(OffersCarouselContentContractForPageTemplate(props) as any)} nocache={nocache} />;
    },
  },
  OFFERS_GROUPED: {
    name: 'OFFERS_GROUPED',
    Component: props => {
      const nocache = props.nocache || getNoCacheFromRequest();
      return <OffersGrouped {...(OffersGroupedContentContractForPageTemplate(props) as any)} nocache={nocache} />;
    },
  },
  OFFERS_SLIM: {
    name: 'OFFERS_SLIM',
    Component: props => {
      const nocache = props.nocache || getNoCacheFromRequest();
      return <OffersSlim {...(OffersSlimContentContractForPageTemplate(props) as any)} nocache={nocache} />;
    },
  },
  ONBOARDING_WIDGET: {
    name: 'ONBOARDING_WIDGET',
    Component: (props: OnboardingProps) => <Onboarding {...props} />,
  },
  OUTRIGHTS_COUPON: {
    name: 'OUTRIGHTS_COUPON',
    Component: (props: OutrightsCouponIndexProps) => {
      const nocache = props.nocache || getNoCacheFromRequest();
      return <OutrightsCoupon {...props} nocache={nocache} />;
    },
  },
  PARENT_LINK_HEADER: {
    name: 'PARENT_LINK_HEADER',
    Component: (props: ParentLinkHeaderProps) => <ParentLinkHeader {...props} />,
  },
  REVIEWS_SECTION: {
    name: 'REVIEWS_SECTION',
    Component: props => <ReviewsSection {...props} />,
  },
  PREMIUM_SIGNUP: {
    name: 'PREMIUM_SIGNUP',
    Component: (props: PremiumSignupProps) => {
      const { myocData } = useMyOCAuth();
      const dataApiEndpoint = getServerCxApiUrl() || '';
      const apiKey = ENV.OC_API_KEY[props.repub as keyof typeof ENV.OC_API_KEY];
      const stripePublishableKey = ENV.STRIPE_PUBLISHABLE_KEY[props.repub] ?? '';
      const deviceId = myocData.deviceID;
      const subscriptionProducts =
        props.repub in SUBSCRIPTION_CONFIGS
          ? (SUBSCRIPTION_CONFIGS[props.repub as keyof typeof SUBSCRIPTION_CONFIGS].products as Record<
              string,
              { isDefaultWhenSingleOption: boolean }
            >)
          : null;
      const productId = subscriptionProducts
        ? (Object.keys(subscriptionProducts).find(id => subscriptionProducts[id]?.isDefaultWhenSingleOption) ?? '')
        : '';

      return (
        <PremiumSignup
          {...props}
          apiKey={apiKey}
          dataApiEndpoint={dataApiEndpoint}
          deviceId={deviceId}
          productId={productId}
          returnUrl={props.requestUrl}
          stripePublishableKey={stripePublishableKey}
        />
      );
    },
  },
  SUBEVENT_HEADER: {
    name: 'SUBEVENT_HEADER',
    Component: props => {
      const subeventId = getSubeventId(props.subeventMeta);
      const nocache = props.nocache || getNoCacheFromRequest();
      const propsWithSubeventId = { ...props, subeventId, nocache };

      return <SubeventHeader {...propsWithSubeventId} />;
    },
  },
} satisfies ClientComponentMap;
